import { vec3 } from 'gl-matrix';

import { NavigationAwareObject } from '../NavigationAwareObject';
import { NavigationBehaviourBase } from './NavigationBehaviour';
import { RenderObject } from '../RenderObject';
import { PathNode } from '../../navigation-mesh';
import { toRadian } from '../../math';

export type PlayerInputState = {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  running: boolean;
};

export type PlayerControlConfig = {
  walkSpeed: number;
  runSpeed: number;
  getCameraYaw: () => number;
  getInputState: () => PlayerInputState;
};

export class PlayerControlBehaviour extends NavigationBehaviourBase {
  private navObject?: NavigationAwareObject;
  private lastFrameTime: number = 0;
  private moving = false;
  private currentNode?: PathNode;

  constructor(
    private entity: RenderObject,
    private config: PlayerControlConfig,
  ) {
    super();
  }

  attach(navObject: NavigationAwareObject) {
    this.navObject = navObject;
    navObject.movement.clear();
    this.currentNode = navObject.movement.currentNode?.node;
    this.lastFrameTime = performance.now();
    this.moving = false;
  }

  detach(navObject: NavigationAwareObject) {
    if (this.moving) {
      this.setMoveAnimation(this.entity, 'Idle');
    }
    this.navObject = undefined;
    this.currentNode = undefined;
    this.moving = false;
  }

  /**
   * Find which node contains a given XZ position by checking the current node
   * first, then its neighbours (graph walk). This avoids false boundaries from
   * global spatial queries and naturally follows the nav mesh connectivity
   * (including stairs and multi-level areas).
   */
  private findNodeAt(x: number, z: number): PathNode | undefined {
    if (!this.currentNode) return undefined;
    const currentY = (this.entity.position as vec3)[1];

    // Check current node first (no Y filter — player is already here)
    if (this.nodeContainsXZ(this.currentNode, x, z)) {
      return this.currentNode;
    }

    // Check immediate neighbours with Y-proximity guard
    for (const neighbour of this.currentNode.neighbours) {
      if (this.nodeContainsXZ(neighbour, x, z) && this.nodeIsHeightReachable(neighbour, x, z, currentY)) {
        return neighbour;
      }
    }

    // Check neighbours-of-neighbours (handles crossing small nodes like
    // stair treads or narrow corridors in a single frame)
    for (const neighbour of this.currentNode.neighbours) {
      for (const nn of neighbour.neighbours) {
        if (nn !== this.currentNode && this.nodeContainsXZ(nn, x, z) && this.nodeIsHeightReachable(nn, x, z, currentY)) {
          return nn;
        }
      }
    }

    return undefined;
  }

  private nodeContainsXZ(node: PathNode, x: number, z: number): boolean {
    const bb = node.getBoundingBox();
    return x >= bb.min[0] && x <= bb.max[0] && z >= bb.min[2] && z <= bb.max[2];
  }

  /**
   * Returns true if the actual floor at (x, z) inside `node` is reachable from
   * the player's current Y. Queries the VolumeGrid for the real surface height
   * rather than relying on `node.min[1]`, which is only the lowest corner of the
   * entire node polygon and gives false negatives on large sloped terrain nodes.
   * Falls back to `node.min[1]` when the VolumeGrid is unavailable (unit tests).
   */
  private nodeIsHeightReachable(node: PathNode, x: number, z: number, currentY: number): boolean {
    const maxStep = this.navObject?.navigationData.navigation.maxNeighbourStepHeight ?? Infinity;
    const grid = this.navObject?.navigationData.volumeGrid;

    if (grid) {
      // For terrain-only areas with no volgrid tile, the navmesh itself guarantees
      // reachability — maxNeighbourStepHeight is an indoor step-ledge limit and
      // must not gate movement on open-world terrain slopes.
      if (!grid.hasTileAt(x, z)) {
        return true;
      }
      const floorY = grid.getFloorHeightBelow(x, node.max[1] as number, z);
      if (floorY !== null) {
        const delta = floorY - currentY;
        return delta > 0 ? delta <= maxStep : -delta <= maxStep;
      }
    }

    const nodeFloor = node.min[1] as number;
    const delta = nodeFloor - currentY;
    return delta > 0 ? delta <= maxStep : -delta <= maxStep;
  }

  private applyMove(
    position: vec3,
    newX: number,
    newZ: number,
    node: PathNode,
    direction: vec3,
    running: boolean,
  ) {
    position[0] = newX;
    position[2] = newZ;

    const grid = this.navObject!.navigationData.volumeGrid;
    const maxStep = this.navObject?.navigationData.navigation.maxNeighbourStepHeight ?? Infinity;
    const currentY = position[1] as number;
    // No real volgrid tile here — terrain fallback covers the floor query.
    // Skip the upward-snap limit so the player tracks the terrain surface on slopes.
    const noTile = !!grid && !grid.hasTileAt(position[0] as number, position[2] as number);

    const surfaceHeight = grid?.getFloorHeightBelow(
      position[0] as number,
      currentY + maxStep,
      position[2] as number,
    );
    if (surfaceHeight !== null && surfaceHeight !== undefined) {
      const upwardSnap = surfaceHeight - currentY;
      if (noTile || upwardSnap <= maxStep) {
        if (this.navObject!.physicsBody) {
          this.navObject!.physicsBody.surfaceHeight = surfaceHeight;
          this.navObject!.physicsBody.minFloorHeight = surfaceHeight;
        } else {
          position[1] = surfaceHeight;
        }
      }
    }

    this.currentNode = node;
    this.navObject!.movement.setCurrentNode({
      position: vec3.clone(position),
      node,
    });

    this.entity.rotateTo(direction);
    this.setMoveAnimation(this.entity, running ? 'Run' : 'Walk');
    this.moving = true;
  }

  step() {
    if (!this.navObject) return;

    const deltaTime = this.advanceFrame();
    if (deltaTime === null) return;

    this.syncWithPathfinder();

    const input = this.config.getInputState();
    const direction = this.readInputDirection(input);
    if (!direction) {
      this.goIdle();
      return;
    }

    const speed = input.running ? this.config.runSpeed : this.config.walkSpeed;
    if (!this.tryMove(direction, speed * deltaTime, input.running)) {
      this.goIdle();
    }
  }

  /**
   * Advance the frame timer and return the delta in seconds. Returns `null`
   * when the delta is non-positive or large enough to indicate a stall (paused
   * tab, breakpoint), so the caller skips the frame entirely.
   */
  private advanceFrame(): number | null {
    const now = performance.now();
    const deltaTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    if (deltaTime <= 0 || deltaTime > 0.5) return null;
    return deltaTime;
  }

  /**
   * Reset any in-flight pathfinder movement and re-sync `currentNode` from
   * whatever the pathfinder left us on. The player controller and the
   * pathfinder both write to the entity's position, so this keeps our local
   * graph cursor consistent before reading input.
   */
  private syncWithPathfinder(): void {
    if (this.navObject!.movement.isActive) {
      this.navObject!.movement.clear();
    }
    if (this.navObject!.movement.currentNode?.node) {
      this.currentNode = this.navObject!.movement.currentNode.node;
    }
    // Re-acquire a node when we don't have one — this handles two cases:
    // (a) player spawned before navmesh tiles finished loading async,
    // (b) player walked to the edge of the loaded ring and new tiles just arrived.
    if (!this.currentNode) {
      this.currentNode = this.navObject!.getPathNode(this.entity.position, 10);
    }
  }

  /**
   * Convert the raw input + camera yaw into a normalized world-space movement
   * direction on the XZ plane. Returns `null` when there is no input, signaling
   * that the entity should transition to idle.
   */
  private readInputDirection(input: PlayerInputState): vec3 | null {
    const inputForward = (input.forward ? 1 : 0) - (input.backward ? 1 : 0);
    const inputRight = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    if (inputForward === 0 && inputRight === 0) return null;

    const yawRad = toRadian(this.config.getCameraYaw());
    const sinYaw = Math.sin(yawRad);
    const cosYaw = Math.cos(yawRad);

    // Forward = [sin(yaw), 0, cos(yaw)]
    // Right = cross(forward, up) = [-cos(yaw), 0, sin(yaw)]
    const direction = vec3.fromValues(
      sinYaw * inputForward - cosYaw * inputRight,
      0,
      cosYaw * inputForward + sinYaw * inputRight,
    );
    vec3.normalize(direction, direction);
    return direction;
  }

  /**
   * Try to move the entity by `distance` along `direction`. Falls back to
   * sliding along each axis when the direct move is blocked. Returns `false`
   * if the entity is fully blocked and didn't move.
   */
  private tryMove(direction: vec3, distance: number, running: boolean): boolean {
    const position = this.entity.position as vec3;
    const newX = position[0] + direction[0] * distance;
    const newZ = position[2] + direction[2] * distance;

    // Validate against nav mesh using graph walk (current node + neighbours)
    const direct = this.findNodeAt(newX, newZ);
    if (direct) {
      this.applyMove(position, newX, newZ, direct, direction, running);
      return true;
    }

    // Blocked — try sliding along each axis
    const slideX = this.findNodeAt(newX, position[2]);
    if (slideX) {
      this.applyMove(position, newX, position[2], slideX, direction, running);
      return true;
    }

    const slideZ = this.findNodeAt(position[0], newZ);
    if (slideZ) {
      this.applyMove(position, position[0], newZ, slideZ, direction, running);
      return true;
    }

    return false;
  }

  private goIdle(): void {
    if (this.moving) {
      this.moving = false;
      this.setMoveAnimation(this.entity, 'Idle');
    }
  }
}
